import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShop } from '../context/ShopContext';
import { 
  Save, 
  Upload, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Clock,
  Instagram,
  Facebook,
  Twitter,
  Award,
  X,
  Image as ImageIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import fileUploadService from '../services/fileUploadService';
import api from '../config/api';
import BusinessHoursEditor from '../components/BusinessHoursEditor';

const ProfileEdit = () => {
  const { t } = useTranslation();
  const { shop, fetchShop } = useShop();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    description: '',
    bio: '',
    logoUrl: '',
    bannerUrl: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    openingHours: '',
    specialties: '',
    website: '',
    instagram: '',
    facebook: '',
    twitter: ''
  });

  useEffect(() => {
    if (shop) {
      setProfileData({
        name: shop.name || '',
        description: shop.description || '',
        bio: shop.bio || '',
        logoUrl: shop.logoUrl || '',
        bannerUrl: shop.bannerUrl || '',
        email: shop.email || '',
        phone: shop.phone || '',
        address: shop.address || '',
        city: shop.city || '',
        country: shop.country || '',
        postalCode: shop.postalCode || '',
        openingHours: shop.openingHours || '',
        specialties: shop.specialties || '',
        website: shop.website || '',
        instagram: shop.instagram || '',
        facebook: shop.facebook || '',
        twitter: shop.twitter || ''
      });
    }
  }, [shop]);

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      
      const response = await api.put('/shop/profile', profileData);

      if (response.data) {
        await fetchShop();
        toast.success(t('profileEdit.profileUpdated'));
      } else {
        toast.error(t('profileEdit.updateFailed'));
      }
    } catch (error) {
      toast.error(t('profileEdit.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;

    const validation = fileUploadService.validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    try {
      setLoading(true);
      let result;
      
      if (field === 'logoUrl') {
        result = await fileUploadService.uploadLogo(file);
      } else if (field === 'bannerUrl') {
        result = await fileUploadService.uploadBanner(file);
      } else {
        result = await fileUploadService.uploadProductImage(file);
      }
      
      if (result.success) {
        handleInputChange(field, result.data.url);
        toast.success(t('profileEdit.imageUploaded', 'Image uploaded successfully'));
      } else {
        toast.error(result.error || t('profileEdit.imageUploadFailed', 'Failed to upload image'));
      }
    } catch (error) {
      toast.error(t('profileEdit.imageUploadFailed', 'Failed to upload image'));
    } finally {
      setLoading(false);
    }
  };

  const removeImage = (field) => {
    handleInputChange(field, '');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('profileEdit.title', 'Edit Profile')}</h1>
          <p className="text-gray-400">{t('profileEdit.description', 'Update your shop information and settings')}</p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Main Form */}
          <div className="xl:col-span-3 space-y-6">
            {/* Basic Information */}
            <div className="bg-[#1f2937] rounded-xl p-6 border border-gray-700/50">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                <Award className="w-5 h-5 mr-2 text-[#a15507]" />
                {t('profileEdit.basicInfo', 'Basic Information')}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('profileEdit.shopName', 'Shop Name')} *
                  </label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.shopNamePlaceholder', 'Enter your shop name')}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('profileEdit.shortDescription', 'Short Description')}
                  </label>
                  <input
                    type="text"
                    value={profileData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.descriptionPlaceholder', 'Brief description of your shop')}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('profileEdit.bio', 'Bio')}
                  </label>
                  <textarea
                    value={profileData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    rows={4}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors resize-none"
                    placeholder={t('profileEdit.bioPlaceholder', 'Tell customers about your shop')}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('profileEdit.specialties', 'Specialties')}
                  </label>
                  <input
                    type="text"
                    value={profileData.specialties}
                    onChange={(e) => handleInputChange('specialties', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.specialtiesPlaceholder', 'e.g., Specialty Coffee, Pastries, Vegan Options')}
                  />
                  <p className="text-gray-400 text-sm mt-2">{t('profileEdit.specialtiesHint', 'Separate specialties with commas')}</p>
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-[#1f2937] rounded-xl p-6 border border-gray-700/50">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                <ImageIcon className="w-5 h-5 mr-2 text-[#a15507]" />
                {t('profileEdit.images', 'Images')}
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Logo */}
                <div>
                  <label className="block text-white text-sm font-medium mb-3">
                    {t('profileEdit.logo', 'Logo')}
                  </label>
                  <div className="space-y-4">
                    {profileData.logoUrl ? (
                      <div className="relative inline-block">
                        <img 
                          src={profileData.logoUrl} 
                          alt={t('profileEdit.logoAlt', 'Shop logo')} 
                          className="w-32 h-32 rounded-full object-cover border-4 border-gray-600 shadow-lg"
                        />
                        <button
                          onClick={() => removeImage('logoUrl')}
                          className="absolute -top-2 -right-2 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors shadow-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 rounded-full bg-gradient-to-r from-[#a15507] to-[#924e06] flex items-center justify-center border-4 border-gray-600 shadow-lg">
                        <Award className="w-12 h-12 text-white" />
                      </div>
                    )}
                    
                    <div className="space-y-3">
                      <input
                        type="url"
                        value={profileData.logoUrl}
                        onChange={(e) => handleInputChange('logoUrl', e.target.value)}
                        className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                        placeholder={t('profileEdit.logoUrlPlaceholder', 'Or paste image URL')}
                      />
                      <label className="block w-full">
                        <div className="w-full bg-[#374151] hover:bg-[#4b5563] border border-gray-600 hover:border-[#a15507] rounded-lg px-4 py-3 cursor-pointer transition-colors flex items-center justify-center gap-2">
                          <Upload className="w-5 h-5 text-white" />
                          <span className="text-white font-medium">{t('profileEdit.uploadLogo', 'Upload Logo')}</span>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload('logoUrl', e.target.files[0])}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Banner */}
                <div>
                  <label className="block text-white text-sm font-medium mb-3">
                    {t('profileEdit.banner', 'Banner')}
                  </label>
                  <div className="space-y-4">
                    {profileData.bannerUrl ? (
                      <div className="relative">
                        <img 
                          src={profileData.bannerUrl} 
                          alt={t('profileEdit.bannerAlt', 'Shop banner')} 
                          className="w-full h-32 object-cover rounded-lg border-4 border-gray-600 shadow-lg"
                        />
                        <button
                          onClick={() => removeImage('bannerUrl')}
                          className="absolute top-2 right-2 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors shadow-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-full h-32 rounded-lg bg-gradient-to-r from-[#a15507] to-[#924e06] flex items-center justify-center border-4 border-gray-600 shadow-lg">
                        <ImageIcon className="w-12 h-12 text-white" />
                      </div>
                    )}
                    
                    <div className="space-y-3">
                      <input
                        type="url"
                        value={profileData.bannerUrl}
                        onChange={(e) => handleInputChange('bannerUrl', e.target.value)}
                        className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                        placeholder={t('profileEdit.bannerUrlPlaceholder', 'Or paste image URL')}
                      />
                      <label className="block w-full">
                        <div className="w-full bg-[#374151] hover:bg-[#4b5563] border border-gray-600 hover:border-[#a15507] rounded-lg px-4 py-3 cursor-pointer transition-colors flex items-center justify-center gap-2">
                          <Upload className="w-5 h-5 text-white" />
                          <span className="text-white font-medium">{t('profileEdit.uploadBanner', 'Upload Banner')}</span>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload('bannerUrl', e.target.files[0])}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-[#1f2937] rounded-xl p-6 border border-gray-700/50">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-[#a15507]" />
                {t('profileEdit.contactInfo', 'Contact Information')}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    {t('profileEdit.email', 'Email')}
                  </label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.emailPlaceholder', 'contact@yourshop.com')}
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    {t('profileEdit.phone', 'Phone')}
                  </label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.phonePlaceholder', '+1 (555) 123-4567')}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('profileEdit.address', 'Address')}
                  </label>
                  <input
                    type="text"
                    value={profileData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.addressPlaceholder', '123 Main Street')}
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('profileEdit.city', 'City')}
                  </label>
                  <input
                    type="text"
                    value={profileData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.cityPlaceholder', 'New York')}
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('profileEdit.country', 'Country')}
                  </label>
                  <input
                    type="text"
                    value={profileData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.countryPlaceholder', 'United States')}
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('profileEdit.postalCode', 'Postal Code')}
                  </label>
                  <input
                    type="text"
                    value={profileData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.postalCodePlaceholder', '10001')}
                  />
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-[#1f2937] rounded-xl p-6 border border-gray-700/50">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-[#a15507]" />
                {t('profileEdit.businessHours', 'Business Hours')}
              </h2>
              
              <BusinessHoursEditor
                value={profileData.openingHours}
                onChange={(value) => handleInputChange('openingHours', value)}
                t={t}
              />
            </div>

            {/* Social Media */}
            <div className="bg-[#1f2937] rounded-xl p-6 border border-gray-700/50">
              <h2 className="text-xl font-semibold text-white mb-6">{t('profileEdit.socialMedia', 'Social Media')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    <Globe className="w-4 h-4 inline mr-1" />
                    {t('profileEdit.website', 'Website')}
                  </label>
                  <input
                    type="url"
                    value={profileData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.websitePlaceholder', 'https://yourshop.com')}
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    <Instagram className="w-4 h-4 inline mr-1" />
                    {t('profileEdit.instagram', 'Instagram')}
                  </label>
                  <input
                    type="text"
                    value={profileData.instagram}
                    onChange={(e) => handleInputChange('instagram', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.instagramPlaceholder', '@yourshop')}
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    <Facebook className="w-4 h-4 inline mr-1" />
                    {t('profileEdit.facebook', 'Facebook')}
                  </label>
                  <input
                    type="text"
                    value={profileData.facebook}
                    onChange={(e) => handleInputChange('facebook', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.facebookPlaceholder', 'yourshop')}
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    <Twitter className="w-4 h-4 inline mr-1" />
                    {t('profileEdit.twitter', 'Twitter')}
                  </label>
                  <input
                    type="text"
                    value={profileData.twitter}
                    onChange={(e) => handleInputChange('twitter', e.target.value)}
                    className="w-full bg-[#374151] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] focus:ring-1 focus:ring-[#a15507] transition-colors"
                    placeholder={t('profileEdit.twitterPlaceholder', '@yourshop')}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Save Button */}
            <div className="bg-[#1f2937] rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-4">{t('profileEdit.actions', 'Actions')}</h3>
              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="w-full bg-[#a15507] hover:bg-[#924e06] disabled:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                {loading ? t('profileEdit.saving', 'Saving...') : t('profileEdit.saveChanges', 'Save Changes')}
              </button>
            </div>

            {/* Preview */}
            <div className="bg-[#1f2937] rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-4">{t('profileEdit.preview', 'Preview')}</h3>
              <div className="space-y-4">
                <div className="text-center">
                  {profileData.logoUrl ? (
                    <img 
                      src={profileData.logoUrl} 
                      alt={t('profileEdit.logoPreviewAlt', 'Shop logo preview')} 
                      className="w-20 h-20 rounded-full object-cover mx-auto border-4 border-gray-600 shadow-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#a15507] to-[#924e06] mx-auto flex items-center justify-center border-4 border-gray-600 shadow-lg">
                      <Award className="w-10 h-10 text-white" />
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <h4 className="text-white font-semibold text-lg">{profileData.name || t('profileEdit.shopName', 'Shop Name')}</h4>
                  {profileData.description && (
                    <p className="text-gray-400 text-sm mt-1">{profileData.description}</p>
                  )}
                  {profileData.specialties && (
                    <p className="text-[#a15507] text-sm mt-2 font-medium">{profileData.specialties}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-[#1f2937] rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-4">{t('profileEdit.tips', 'Tips')}</h3>
              <div className="space-y-3 text-sm text-gray-400">
                <p>• {t('profileEdit.tip1', 'Use high-quality images for better presentation')}</p>
                <p>• {t('profileEdit.tip2', 'Keep descriptions concise and engaging')}</p>
                <p>• {t('profileEdit.tip3', 'Update business hours regularly')}</p>
                <p>• {t('profileEdit.tip4', 'Include relevant social media links')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEdit;
